import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Brain, Cloud, Link, CheckCircle, XCircle, Sparkles, Loader2, Shield, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Link as RouterLink } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Training() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  
  // Custom API Connection state
  const [customApiEndpoint, setCustomApiEndpoint] = useState("");
  const [customApiKey, setCustomApiKey] = useState("");
  const [connecting, setConnecting] = useState(false);
  
  // Emergent ML state
  const [trainingEmergent, setTrainingEmergent] = useState(false);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/model/status`);
      setMetrics(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error fetching model status:", error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleConnectCustomAPI = async () => {
    if (!customApiEndpoint.trim()) {
      toast.error("Please enter your API endpoint URL");
      return;
    }
    
    try {
      setConnecting(true);
      const response = await axios.post(`${API}/model/connect-custom-api`, {
        endpoint_url: customApiEndpoint.trim(),
        api_key: customApiKey.trim() || null,
        response_field: 'fraud_score'
      });
      
      if (response.data.custom_api_enabled) {
        toast.success("Connected! Your Custom API is now active.");
        setCustomApiEndpoint("");
        setCustomApiKey("");
        setActiveTab(null);
      } else {
        toast.error(response.data.message || "Failed to connect");
      }
      await fetchMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to connect to API");
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      // Try custom API disconnect first, then general disconnect
      try {
        await axios.post(`${API}/model/disconnect-custom-api`);
      } catch {
        await axios.post(`${API}/model/disconnect`);
      }
      toast.success("Disconnected. Using Emergent ML or rule-based detection.");
      await fetchMetrics();
    } catch (error) {
      toast.error("Failed to disconnect");
    }
  };

  const handleTrainEmergent = async () => {
    try {
      setTrainingEmergent(true);
      toast.info("Training Emergent ML model... This may take a moment.");
      
      const response = await axios.post(`${API}/model/train-emergent`);
      
      if (response.data.status === 'success') {
        toast.success(`Emergent ML model trained! Using ${response.data.model_name}`);
        setActiveTab(null);
      } else {
        toast.error(response.data.message || "Training failed");
      }
      await fetchMetrics();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to train model");
    } finally {
      setTrainingEmergent(false);
    }
  };

  const getModelBadge = () => {
    if (!metrics) return null;
    
    if (metrics.model_type === 'custom_api') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
          <Link size={10} className="mr-1" /> Custom API Active
        </Badge>
      );
    } else if (metrics.model_type === 'emergent_ml') {
      return (
        <Badge className="bg-blue-100 text-blue-700 text-[10px]">
          <Sparkles size={10} className="mr-1" /> Emergent ML Active
        </Badge>
      );
    } else if (metrics.hf_enabled) {
      return (
        <Badge className="bg-purple-100 text-purple-700 text-[10px]">
          <CheckCircle size={10} className="mr-1" /> Hugging Face Connected
        </Badge>
      );
    } else {
      return <Badge className="bg-zinc-100 text-zinc-600 text-[10px]">Rule-Based Active</Badge>;
    }
  };

  const getModelTitle = () => {
    if (!metrics) return "Loading...";
    if (metrics.model_type === 'custom_api') return "Custom API Active";
    if (metrics.model_type === 'emergent_ml') return "Emergent ML Active";
    if (metrics.hf_enabled) return "Hugging Face Active";
    return "Rule-Based Detection";
  };

  const isMLActive = metrics?.hf_enabled || metrics?.model_type === 'emergent_ml' || metrics?.model_type === 'custom_api';

  return (
    <div className="p-4" data-testid="training-page">
      {/* Quick Overview */}
      <Card className="p-4 bg-zinc-50 border-zinc-200 mb-6" data-testid="quick-overview">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold text-zinc-900 flex items-center gap-2">
            <Shield size={16} className="text-purple-600" />
            3 Detection Approaches
          </h2>
          <RouterLink 
            to="/guide" 
            className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <BookOpen size={12} />
            Full Guide
          </RouterLink>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="p-3 bg-white rounded-lg border border-zinc-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-zinc-200 flex items-center justify-center text-[10px] font-bold text-zinc-600">1</div>
              <span className="text-xs font-semibold text-zinc-800">Rule-Based</span>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">
              Default mode using predefined rules. No training required.
            </p>
            <div className="mt-2 px-2 py-1 bg-zinc-100 rounded text-[9px] text-zinc-500 inline-block">
              Always available
            </div>
          </div>

          <div className="p-3 bg-white rounded-lg border border-blue-100 ring-1 ring-blue-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-[10px] font-bold text-white">2</div>
              <span className="text-xs font-semibold text-zinc-800">Emergent ML</span>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">
              Pre-trained Gradient Boosting model. ~83% accuracy. Ready to use!
            </p>
            <div className="mt-2 px-2 py-1 bg-blue-100 rounded text-[9px] text-blue-600 inline-block">
              Pre-trained
            </div>
          </div>

          <div className="p-3 bg-white rounded-lg border border-zinc-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-[10px] font-bold text-white">3</div>
              <span className="text-xs font-semibold text-zinc-800">Custom API</span>
            </div>
            <p className="text-[10px] text-zinc-600 leading-relaxed">
              Connect any REST endpoint (SageMaker, Vertex AI, self-hosted).
            </p>
            <div className="mt-2 px-2 py-1 bg-emerald-100 rounded text-[9px] text-emerald-600 inline-block">
              Enterprise
            </div>
          </div>
        </div>

        {/* Score Thresholds */}
        <div className="flex flex-wrap gap-4 text-[10px] text-zinc-600">
          <span className="font-semibold text-zinc-700">Thresholds:</span>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span><strong>&gt;0.70</strong> BLOCKED</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
            <span><strong>0.5-0.70</strong> FLAGGED</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
            <span><strong>&lt;0.5</strong> APPROVED</span>
          </div>
        </div>
      </Card>

      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-zinc-950">
          Model Training
        </h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Choose how to enable ML-based fraud detection
        </p>
      </div>

      {/* Current Detection Mode */}
      <Card className="p-5 border border-zinc-200 rounded-lg shadow-none mb-6" data-testid="detection-mode-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              metrics?.model_type === 'emergent_ml' ? 'bg-gradient-to-br from-blue-500 to-purple-600' :
              metrics?.hf_enabled ? 'bg-purple-600' : 'bg-zinc-900'
            }`}>
              {metrics?.model_type === 'emergent_ml' ? (
                <Sparkles size={20} className="text-white" />
              ) : (
                <Brain size={20} className="text-white" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-zinc-950">
                {getModelTitle()}
              </h3>
              <div className="mt-1">
                {getModelBadge()}
              </div>
            </div>
          </div>
          
          {isMLActive && (
            <Button
              onClick={handleDisconnect}
              variant="outline"
              size="sm"
              className="h-8 text-xs border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle size={14} className="mr-1" />
              Disconnect
            </Button>
          )}
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-4 p-3 bg-zinc-50 rounded-lg mt-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-900">{((metrics?.accuracy || 1.0) * 100).toFixed(0)}%</p>
            <p className="text-[10px] text-zinc-500 uppercase">Accuracy</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-900">{((metrics?.precision || 1.0) * 100).toFixed(0)}%</p>
            <p className="text-[10px] text-zinc-500 uppercase">Precision</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-900">{((metrics?.recall || 1.0) * 100).toFixed(0)}%</p>
            <p className="text-[10px] text-zinc-500 uppercase">Recall</p>
          </div>
        </div>
        
        {/* Model info when active */}
        {metrics?.model_name && (
          <div className="mt-3 p-2 bg-zinc-100 rounded text-xs text-zinc-600">
            Model: <span className="font-medium">{metrics.model_name}</span>
          </div>
        )}
      </Card>

      {/* Choose Training Option - Only show if not connected */}
      {!isMLActive && (
        <>
          <h3 className="text-sm font-semibold text-zinc-700 mb-3">Choose ML Option</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Option A: Custom API */}
            <Card 
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                activeTab === 'customapi' 
                  ? 'border-emerald-500 bg-emerald-50' 
                  : 'border-zinc-200 hover:border-emerald-300'
              }`}
              onClick={() => setActiveTab(activeTab === 'customapi' ? null : 'customapi')}
              data-testid="option-customapi"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                  <Cloud size={16} className="text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900">Custom API</h4>
                  <p className="text-[10px] text-zinc-500">Any REST endpoint</p>
                </div>
              </div>
              <p className="text-xs text-zinc-600">
                Connect your ML service (SageMaker, Vertex AI, Azure ML, self-hosted).
              </p>
            </Card>

            {/* Option B: Emergent ML */}
            <Card 
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                activeTab === 'emergent' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-zinc-200 hover:border-blue-300'
              }`}
              onClick={() => setActiveTab(activeTab === 'emergent' ? null : 'emergent')}
              data-testid="option-emergent"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Sparkles size={16} className="text-white" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900">Emergent ML</h4>
                  <p className="text-[10px] text-zinc-500">Pre-trained model</p>
                </div>
              </div>
              <p className="text-xs text-zinc-600">
                Use pre-trained Gradient Boosting model for fraud detection.
              </p>
            </Card>
          </div>

          {/* Custom API Flow */}
          {activeTab === 'customapi' && (
            <Card className="p-5 bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-lg mb-4" data-testid="customapi-flow">
              <h4 className="text-base font-bold text-emerald-900 mb-3">Connect Custom API</h4>
              <p className="text-xs text-emerald-700 mb-4">
                Enter your REST API endpoint URL. Your API should accept POST requests with transaction features and return a fraud score.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-emerald-700 font-medium mb-1 block">Endpoint URL *</label>
                  <Input
                    type="url"
                    placeholder="https://your-api.com/predict"
                    value={customApiEndpoint}
                    onChange={(e) => setCustomApiEndpoint(e.target.value)}
                    className="h-10 text-sm border-emerald-200"
                    data-testid="custom-api-endpoint-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-emerald-700 font-medium mb-1 block">API Key (optional)</label>
                  <Input
                    type="password"
                    placeholder="Bearer token or API key"
                    value={customApiKey}
                    onChange={(e) => setCustomApiKey(e.target.value)}
                    className="h-10 text-sm border-emerald-200"
                    data-testid="custom-api-key-input"
                  />
                </div>
                <div className="p-3 bg-white border border-emerald-100 rounded-lg">
                  <p className="text-[10px] text-emerald-700 font-medium mb-2">Expected API Format:</p>
                  <pre className="text-[9px] text-emerald-600 bg-emerald-50 p-2 rounded overflow-x-auto">
{`POST /predict
{
  "amount": 500.0,
  "hour": 14,
  "merchant_risk_score": 0.3,
  ...
}

Response: { "fraud_score": 0.75 }`}
                  </pre>
                </div>
                <Button
                  onClick={handleConnectCustomAPI}
                  disabled={connecting || !customApiEndpoint.trim()}
                  className="w-full h-10 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="connect-custom-api-btn"
                >
                  <Link size={14} className="mr-1" />
                  {connecting ? "Testing Connection..." : "Connect API"}
                </Button>
              </div>
            </Card>
          )}

          {/* Emergent ML Flow */}
          {activeTab === 'emergent' && (
            <Card className="p-5 bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg mb-4" data-testid="emergent-flow">
              <h4 className="text-base font-bold text-blue-900 mb-3">Train with Emergent ML</h4>
              <p className="text-xs text-blue-700 mb-4">
                We'll generate synthetic fraud data and train a <strong>Gradient Boosting</strong> model for intelligent fraud classification.
              </p>
              
              <div className="p-3 bg-white border border-blue-100 rounded-lg mb-4">
                <div className="flex items-center gap-2 text-xs text-blue-800">
                  <CheckCircle size={14} className="text-blue-500" />
                  <span>5,000 synthetic labeled transactions</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-800 mt-1">
                  <CheckCircle size={14} className="text-blue-500" />
                  <span>Mini Gradient Boosting (XGBoost-style)</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-800 mt-1">
                  <CheckCircle size={14} className="text-blue-500" />
                  <span>Zero configuration needed</span>
                </div>
              </div>

              <Button
                onClick={handleTrainEmergent}
                disabled={trainingEmergent}
                className="w-full h-10 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="train-emergent-btn"
              >
                {trainingEmergent ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Training...
                  </>
                ) : (
                  <>
                    <Sparkles size={14} className="mr-2" />
                    Train with Emergent
                  </>
                )}
              </Button>
            </Card>
          )}
        </>
      )}

      {/* Info when ML is active */}
      {isMLActive && (
        <Card className={`p-4 border rounded-lg ${
          metrics?.model_type === 'emergent_ml' 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-purple-50 border-purple-200'
        }`}>
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className={
              metrics?.model_type === 'emergent_ml' ? 'text-blue-600' : 'text-purple-600'
            } />
            <div>
              <h4 className={`text-sm font-semibold ${
                metrics?.model_type === 'emergent_ml' ? 'text-blue-900' : 'text-purple-900'
              }`}>
                ML Model Active
              </h4>
              <p className={`text-xs mt-1 ${
                metrics?.model_type === 'emergent_ml' ? 'text-blue-700' : 'text-purple-700'
              }`}>
                {metrics?.model_type === 'emergent_ml' 
                  ? 'Emergent ML (GradientBoost) is handling fraud predictions.'
                  : 'Your Hugging Face model is handling fraud predictions.'
                }
                {' '}Click Disconnect to revert to rule-based detection.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
